import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';

const EmailVerification = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [message, setMessage] = useState('');
    const [hasAttempted, setHasAttempted] = useState(false);

    useEffect(() => {
        const verifyEmail = async () => {
            // Prevent multiple calls
            if (hasAttempted) {
                return;
            }
            setHasAttempted(true);

            try {
                setStatus('verifying');
                setMessage('Weryfikujemy Twój email...');

                // Call the Django endpoint with GET method using apiService
                const response = await apiService.get(`verify-email/${token}/`);
                const data = response.data;

                if (response.status === 200 && data.success) {
                    setStatus('success');
                    setMessage(data.message || 'Email został pomyślnie zweryfikowany! Możesz się teraz zalogować.');
                    
                    // Redirect to login after 3 seconds
                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                } else {
                    setStatus('error');
                    setMessage(data.error || 'Wystąpił błąd podczas weryfikacji emaila.');
                }
            } catch (error) {
                console.error('Email verification error:', error);
                setStatus('error');
                
                if (error.response?.data?.error) {
                    setMessage(error.response.data.error);
                } else if (error.response?.status === 404) {
                    setMessage('Endpoint weryfikacji nie został znaleziony.');
                } else if (error.response?.status >= 500) {
                    setMessage('Błąd serwera. Spróbuj ponownie później.');
                } else {
                    setMessage('Wystąpił błąd podczas weryfikacji emaila. Spróbuj ponownie.');
                }
            }
        };

        if (token && !hasAttempted) {
            verifyEmail();
        } else if (!token) {
            setStatus('error');
            setMessage('Nieprawidłowy token weryfikacyjny.');
        }
    }, [token, navigate, hasAttempted]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto h-12 w-12 flex items-center justify-center">
                        {status === 'verifying' && (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        )}
                        {status === 'success' && (
                            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                        {status === 'error' && (
                            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Weryfikacja emaila
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {message}
                    </p>
                </div>

                {status === 'success' && (
                    <div className="rounded-md bg-green-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800">
                                    Twoje konto zostało aktywowane!
                                </p>
                                <p className="mt-1 text-sm text-green-700">
                                    Zostaniesz przekierowany do strony logowania za kilka sekund...
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-red-800">
                                    Błąd weryfikacji
                                </p>
                                <div className="mt-2">
                                    <button
                                        onClick={() => navigate('/login')}
                                        className="text-sm bg-red-100 text-red-800 rounded-md px-2 py-1 hover:bg-red-200"
                                    >
                                        Przejdź do logowania
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-6">
                    <button
                        onClick={() => navigate('/login')}
                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Przejdź do logowania
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;